// The interaction stream a person page and an organisation page both show (BUILD-BRIEF §6 —
// interactions agent). One row per interaction, grouped by date, dressed in the mockup's
// `.tl-row` idiom (public/styles/main.css): date once per date group in tabular sans, a kind
// micro-label, the title, a `.src-ref` when the interaction carries a source, and a summary
// line underneath. `row--new` marks anything newer than the viewer's last visit.
//
// `LogTouchpointModal` lives here too — it is the one place "kind, date, title, summary,
// participants, organisation" is built, and Today, the person brief and the organisation brief
// all open the SAME modal rather than three copies drifting apart.
import { useEffect, useMemo, useState } from 'react';
import { createInteraction } from '../api/interactions.js';
import { request, ApiError } from '../api/client.js';
import { Sheet } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { useToast } from './Toast.js';
import TranscriptUpload from './TranscriptUpload.js';
import type { InteractionDetail, InteractionKind, ParticipantRole } from '../../shared/types.js';
import type { PersonTimelineItem } from '../api/persons.js';
import './Timeline.de.js';
import './Timeline.fr.js';

const KINDS: InteractionKind[] = ['mail_in', 'mail_out', 'meeting', 'call', 'note', 'post', 'signal'];
const KIND_LABEL: Record<InteractionKind, string> = {
  mail_in: 'Mail in', mail_out: 'Mail out', meeting: 'Meeting', call: 'Call',
  note: 'Note', post: 'Post', signal: 'Signal',
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

// ── the stream ───────────────────────────────────────────────────────────────

export interface TimelineProps {
  /** The agreed cross-agent shape (GET /api/persons/:id, declared in api/persons.ts). Carries
   *  `is_newest` rather than participants/created_at/source_ref — those live only on the raw
   *  `InteractionDetail` a fresh POST returns, not on this rolled-up read shape. */
  items: PersonTimelineItem[];
  className?: string;
}

export default function Timeline({ items, className }: TimelineProps) {
  const t = useT();
  const { fmtDate } = useLocale();

  if (!items.length) {
    return (
      <Sheet clip pad className={className}>
        <p className="zone-empty">{t('Nothing logged here yet. A meeting, a call, a mail, a note — every touchpoint with provenance becomes a row.')}</p>
      </Sheet>
    );
  }

  let lastDate = '';
  return (
    <Sheet clip className={className}>
      {items.map((it) => {
        const showDate = it.occurred_on !== lastDate;
        lastDate = it.occurred_on;
        return (
          <div key={it.id} className={`tl-row${it.is_newest ? ' row--new' : ''}`}>
            <div className="tl-date">
              {showDate ? fmtDate(it.occurred_on) : ''}
              {it.occurred_at && <><br />{it.occurred_at.slice(0, 5)}</>}
              {!it.date_confirmed && <span title={t('Date not confirmed')}> ?</span>}
            </div>
            <div>
              <span className="tl-kind">{t(KIND_LABEL[it.kind as InteractionKind])}</span>
              <span className="tl-title">{it.title}</span>
              {it.ref && <span className="src-ref" style={{ color: 'var(--ink-4)', marginLeft: 6 }}>{it.ref}</span>}
              {(it.summary || it.organisation_name) && (
                <div className="tl-sum">
                  {it.organisation_name && <>{it.organisation_name}{it.summary && ' · '}</>}
                  {it.summary}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </Sheet>
  );
}

// ── log a touchpoint ────────────────────────────────────────────────────────

interface PersonHit { id: number; ref: string; name: string; }
interface OrgHit { id: number; ref: string; name: string; }

function useDebouncedSearch<T>(fetcher: (q: string) => Promise<T[]>, query: string, minLen = 2): T[] {
  const [hits, setHits] = useState<T[]>([]);
  useEffect(() => {
    if (query.trim().length < minLen) { setHits([]); return; }
    let cancelled = false;
    const id = setTimeout(() => {
      fetcher(query.trim()).then((r) => { if (!cancelled) setHits(r); }).catch(() => { if (!cancelled) setHits([]); });
    }, 200);
    return () => { cancelled = true; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  return hits;
}

interface ParticipantDraft { person_id?: number; raw_name?: string; role: ParticipantRole; label: string; }

export interface LogTouchpointModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fills one participant when opened from a person brief. */
  defaultPersonId?: number | null;
  defaultPersonName?: string | null;
  /** Pre-fills the organisation when opened from an organisation brief. */
  defaultOrganisationId?: number | null;
  defaultOrganisationName?: string | null;
  onLogged?: (interaction: InteractionDetail) => void;
}

/**
 * The one "Log a touchpoint" surface. Kind, date, title, summary, participants (searched by
 * name, or added as a raw name when no person matches yet), organisation. Opened from Today,
 * a person brief or an organisation brief — always the same shape, always the same POST.
 */
export function LogTouchpointModal({
  open, onClose, defaultPersonId, defaultPersonName, defaultOrganisationId, defaultOrganisationName, onLogged,
}: LogTouchpointModalProps) {
  const t = useT();
  const toast = useToast();
  const [kind, setKind] = useState<InteractionKind>('note');
  const [occurredOn, setOccurredOn] = useState(todayIso());
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [personQuery, setPersonQuery] = useState('');
  const [participants, setParticipants] = useState<ParticipantDraft[]>([]);
  const [orgQuery, setOrgQuery] = useState('');
  const [org, setOrg] = useState<OrgHit | null>(null);
  const [saving, setSaving] = useState(false);

  const personHits = useDebouncedSearch<PersonHit>(
    (q) => request<{ items: PersonHit[] }>(`/persons?q=${encodeURIComponent(q)}`).then((r) => r.items),
    personQuery,
  );
  const orgHits = useDebouncedSearch<OrgHit>(
    (q) => request<{ items: OrgHit[] }>(`/organisations?q=${encodeURIComponent(q)}`).then((r) => r.items),
    orgQuery,
  );

  useEffect(() => {
    if (!open) return;
    setKind('note');
    setOccurredOn(todayIso());
    setTitle('');
    setSummary('');
    setPersonQuery('');
    setParticipants(
      defaultPersonId != null
        ? [{ person_id: defaultPersonId, role: 'attendee', label: defaultPersonName ?? `#${defaultPersonId}` }]
        : [],
    );
    setOrgQuery('');
    setOrg(defaultOrganisationId != null ? { id: defaultOrganisationId, ref: '', name: defaultOrganisationName ?? `#${defaultOrganisationId}` } : null);
  }, [open, defaultPersonId, defaultPersonName, defaultOrganisationId, defaultOrganisationName]);

  const canSave = useMemo(() => title.trim().length > 0 && occurredOn.length === 10, [title, occurredOn]);

  const addPerson = (hit: PersonHit) => {
    if (participants.some((p) => p.person_id === hit.id)) return;
    setParticipants((prev) => [...prev, { person_id: hit.id, role: 'attendee', label: hit.name }]);
    setPersonQuery('');
  };
  const addRawName = () => {
    const name = personQuery.trim();
    if (!name) return;
    setParticipants((prev) => [...prev, { raw_name: name, role: 'attendee', label: name }]);
    setPersonQuery('');
  };
  const removeParticipant = (idx: number) => setParticipants((prev) => prev.filter((_, i) => i !== idx));

  if (!open) return null;

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const created = await createInteraction({
        kind, occurred_on: occurredOn, title: title.trim(), summary: summary.trim() || null,
        organisation_id: org?.id ?? null,
        participants: participants.map((p) => ({ person_id: p.person_id ?? null, raw_name: p.raw_name ?? null, role: p.role })),
      });
      toast('ok', t('Touchpoint logged.'));
      onLogged?.(created);
      onClose();
    } catch (err) {
      toast('err', err instanceof ApiError ? err.message : t('Could not save the touchpoint.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t('Log a touchpoint')}</h2>
          <button className="close-btn" onClick={onClose} aria-label={t('Close')}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">{t('Kind')}</label>
            <select className="form-select" value={kind} onChange={(e) => setKind(e.target.value as InteractionKind)}>
              {KINDS.map((k) => <option key={k} value={k}>{t(KIND_LABEL[k])}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{t('Date')}</label>
            <input className="form-input" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('Title')}</label>
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('What happened, in a few words')} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('Summary')}</label>
            <textarea className="form-input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
          </div>
          {/* Uploading IS the save: the route writes the meeting interaction with the transcript
              as its body, so the modal closes on success — Save afterwards would log a second. */}
          {kind === 'meeting' && (
            <TranscriptUpload
              occurredOn={occurredOn}
              title={title}
              personIds={participants.map((p) => p.person_id).filter((id): id is number => id != null)}
              organisationId={org?.id ?? null}
              onUploaded={(r) => { onLogged?.(r.interaction); onClose(); }}
            />
          )}
          <div className="form-group">
            <label className="form-label">{t('Organisation')}</label>
            {org
              ? <div className="chips"><span className="tag">{org.name} <button type="button" className="lnk" onClick={() => setOrg(null)}>{t('Remove')}</button></span></div>
              : (
                <>
                  <input className="form-input" value={orgQuery} onChange={(e) => setOrgQuery(e.target.value)} placeholder={t('Search organisations')} />
                  {orgHits.length > 0 && (
                    <div className="sheet" style={{ marginTop: 4 }}>
                      {orgHits.map((h) => (
                        <button key={h.id} type="button" className="row row--btn" onClick={() => { setOrg(h); setOrgQuery(''); }}>
                          <span>{h.name} <span className="src-ref">{h.ref}</span></span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
          </div>
          <div className="form-group">
            <label className="form-label">{t('Participants')}</label>
            {participants.length > 0 && (
              <div className="chips">
                {participants.map((p, i) => (
                  <span key={i} className="tag">{p.label} <button type="button" className="lnk" onClick={() => removeParticipant(i)}>{t('Remove')}</button></span>
                ))}
              </div>
            )}
            <input className="form-input" style={{ marginTop: 8 }} value={personQuery} onChange={(e) => setPersonQuery(e.target.value)} placeholder={t('Search people, or type a name and add it')} />
            {personHits.length > 0 && (
              <div className="sheet" style={{ marginTop: 4 }}>
                {personHits.map((h) => (
                  <button key={h.id} type="button" className="row row--btn" onClick={() => addPerson(h)}>
                    <span>{h.name} <span className="src-ref">{h.ref}</span></span>
                  </button>
                ))}
              </div>
            )}
            {personQuery.trim().length >= 2 && personHits.length === 0 && (
              <button type="button" className="lnk" style={{ marginTop: 4 }} onClick={addRawName}>{t('Add "{name}" as a name, not yet a person', { name: personQuery.trim() })}</button>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" onClick={save} disabled={!canSave || saving}>{saving ? t('Saving…') : t('Save')}</button>
        </div>
      </div>
    </div>
  );
}
